import { Api } from '../typings/api';
import store from '../store';
import Utils, { HttpHeader } from '.';
import userAgentUtils from './userAgent';
import UrlUtils from './url';
import { MultiAppAuthenticationInfosState, MultiAppAuthenticationCredentials } from '../containers/MultiAppAuthentication/reducer';
import { Server, AuthMethod, ASP_SERVERS } from '../containers/MultiAppAuthentication/constants';
import { Security } from '../containers/Authentication/reducer';
import LocalStorageManager from './localStorage/manager';
import { StoreState } from '../reducers';
import { ProfilePageFields, UserAvatarState } from '../containers/ProfilePage/reducer';

export default class ApiUtils {

	public static isDuplicateFileNameError(errorText: string): boolean {
		const DUPLICATE_NAME_ERROR = 'DUPLICATE_NAME';
		return errorText.includes(DUPLICATE_NAME_ERROR);
	}

	private static get galleryConfig(): Api.Config {
		if (!store) {
			console.error('ApiUtils.galleryConfig() is called before store is created');
		}
		const { galleryInfos } = store.getState();
		if (!galleryInfos.isConfigLoaded) {
			console.error('ApiUtils.galleryConfig() is called before galleryInfos.config is loaded');
		}
		return galleryInfos.config;
	}

	private static get multiAppAuthenticationInfos(): MultiAppAuthenticationInfosState {
		if (!store) {
			console.error('ApiUtils.multiAppAuthenticationInfos() is called before store is created');
		}
		const { multiAppAuthenticationInfos } = store.getState();
		return multiAppAuthenticationInfos;
	}

	public static loginToAsp = (credentials: MultiAppAuthenticationCredentials): Promise<Api.AppLoginResponse> => {
		let loginData: {};
		let url: string;
		const { galleryInfos: { config } } = store.getState();

		if (credentials.method === AuthMethod.Webpublication) {
			loginData = { login: credentials.login, password: credentials.password };
			url = UrlUtils.getLoginToAspUrl(config);
		} else {
			loginData = { org: credentials.company, token: credentials.token };
			url = UrlUtils.getOrgLoginUrl(config);
		}

		return Utils.fetchJson(url, 'POST', true, true, loginData);
	}

	public static get multiAppAuthenticationServer(): Server {
		return this.multiAppAuthenticationInfos.server as Server;
	}

	public static get multiAppAuthenticationToken(): string | null {
		const { credentials: { token = null } = {} } = this.multiAppAuthenticationInfos;
		return token;
	}

	public static get token(): string | null {
		return LocalStorageManager.getToken() || ApiUtils.multiAppAuthenticationToken;
	}

	public static logoutFromAsp = (): Promise<{}> => {
		const { galleryConfig } = ApiUtils;
		return Utils.fetchText(UrlUtils.getLogoutFromAspUrl(galleryConfig), 'POST');
	}

	public static logoutFromApplication = (): Promise<{}> => {
		const { galleryConfig } = ApiUtils;

		// there can be different application urls from ASP_SERVERS and from galleryConfig
		// in this case logout must be called on both of them
		const multiAppAuthenticationAppUrl = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer].driveUrl;
		const isSameAppUrl = Utils.isSameUrl(galleryConfig.applicationUrl, multiAppAuthenticationAppUrl);

		const promises = [
			Utils.fetchText(UrlUtils.getLogoutFromApplication(galleryConfig), 'POST'),
		];

		if (!isSameAppUrl) {
			promises.push(Utils.fetchText(UrlUtils.getLogoutFromApplication(galleryConfig, true), 'POST'));
		}

		return Promise.all(promises);
	}

	public static createAspDemoAccount = (email: string, password: string, phone: string): Promise<Api.CustomerAccount> => {
		const { galleryConfig } = ApiUtils;
		const loginData = { email, password, phone };

		return Utils.fetchJson<Api.CustomerAccount>(UrlUtils.getCreateDemoAccountUrl(galleryConfig), 'POST', true, true, loginData)
			.catch(error => Promise.reject(JSON.parse(error.responseText)));
	}

	public static getOrgAuthMethods = (company: string): Promise<Api.AuthMethodsResponse> => {
		const { galleryConfig } = ApiUtils;

		return Utils.fetchJson<Api.AuthMethodsResponse>(UrlUtils.getAuthMethodsUrl(galleryConfig), 'GET', true, true, { org: company })
			.catch(error => Promise.reject(JSON.parse(error.responseText)));
	}

	// TODO: change return type of authenticate
	public static authenticate = (user: string, pass: string): Promise<{} | void> => {
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.fetchJson(UrlUtils.getLoginUrl(ApiUtils.galleryConfig, user, pass, Security.Excel), 'GET', isAsync);
	}

	public static async checkSession(): Promise<{}> {
		return Utils.fetchJson(UrlUtils.getCheckSessionUrl(ApiUtils.galleryConfig), 'GET');
	}

	public static getSecurityHeaderIfNeeded = (preventSecurityHeader: boolean): HttpHeader[] => {
		const { authentication } = store.getState();
		const { mode } = authentication.userInfos;
		const isSamlAuthenticationType = mode && mode.method === Security.Saml;
		return isSamlAuthenticationType && !preventSecurityHeader
			? [{
				key: 'Access-Control-Allow-Credentials',
				value: 'true',
			}]
			: [];
	}

	public static getSecurityType = () => {
		const galleryConfig = ApiUtils.galleryConfig;
		const url = UrlUtils.getLoginJsonUrl(galleryConfig);
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.promiseXHR('GET', url, [], isAsync)
			.then(response => {
				const securityType = response.getResponseHeader('Security') as Security;
				const samlUrl = response.getResponseHeader('SAML_URL') || '';

				return securityType
					? Promise.resolve({securityType, samlUrl})
					: Promise.resolve(undefined);
			})
			.catch(error => {
				return Promise.reject(error);
			});
	}

	public static getTemplate = (server?: Server): Promise<Api.Template> => {
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.fetchJson(UrlUtils.getTemplateUrl(ApiUtils.galleryConfig, server), 'GET', isAsync);
	}

	public static getConfig = (clientId: number, galleryId: number): Promise<Api.Config> => {
		return Utils.fetchJson(UrlUtils.getConfigUrl(ApiUtils.multiAppAuthenticationServer, clientId, galleryId), 'GET', true, true);
	}

	public static getStatistics = (): Promise<string> => {
		return Utils.fetchText(UrlUtils.getStatisticsUrl(ApiUtils.galleryConfig));
	}

	public static getResolutions = (): Promise<Api.ResolutionGroup> => {
		return Utils.fetchJson(UrlUtils.getResolutionsUrl(ApiUtils.galleryConfig));
	}

	public static getChildren = (driveItemId?: number): Promise<Api.Outcoming> | void => {
		const galleryConfig = ApiUtils.galleryConfig;

		const resolvedDriveItemId = driveItemId || galleryConfig.applicationGallery;

		return Utils.fetchJson(UrlUtils.getChildrenUrl(ApiUtils.galleryConfig, resolvedDriveItemId), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getChildrenMeta = (): Promise<Api.NestedDriveItems> => {
		const { galleryConfig, galleryConfig: { applicationGallery } } = ApiUtils;
		return Utils.fetchJson(UrlUtils.getChildrenMetaUrl(galleryConfig, applicationGallery), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getAppMeta = (): Promise<Api.NestedAppMeta> => {
		const { galleryConfig, galleryConfig: { applicationGallery} } = ApiUtils;
		return Utils.fetchJson(UrlUtils.getAppMetaUrl(galleryConfig, applicationGallery), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getMeta = (driveItemId: number, isAsync: boolean = true, isSecured?: boolean): Promise<Api.DriveFolderInfo> => {
		const dataInUrl: {
			excludeNP?: boolean;
			token?: string | null;
		} = {
			...ApiUtils.getIncludeAllResourcesParam(),
		};

		if (isSecured) {
			dataInUrl.token = ApiUtils.token;
		}

		return Utils.fetchJson(UrlUtils.getMetaUrl(ApiUtils.galleryConfig, driveItemId), 'GET', isAsync, true, dataInUrl);
	}

	public static getContentBlock = (driveItemId: number, block: number): Promise<Api.DriveItemContentBlock> => {
		return Utils.fetchJson(UrlUtils.getContentBlockUrl(ApiUtils.galleryConfig, driveItemId, block), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getNewContents = (sinceDate: Date): Promise<Api.DriveItemContentBlock> => {
		return Utils.fetchJson(UrlUtils.getNewContentsUrl(ApiUtils.galleryConfig, sinceDate));
	}

	public static getSearch = (searchString: string): Promise<Api.DriveItemContentBlock> => {
		return Utils.fetchJson(UrlUtils.getSearchUrl(ApiUtils.galleryConfig, searchString), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getOutcomings = (clientId: number, driveItemId: number): Promise<Api.Outcoming> => {
		return Utils.fetchJson(UrlUtils.getOutcomingsUrl(ApiUtils.galleryConfig, driveItemId));
	}

	public static getAvailableServers = (): Promise<{ [k: string]: Api.ServerInfos }> => {
		return Utils.fetchJson<Api.GetClosestServerResponse>('https://fr.adminzone-secure.net/services/rest/loginWs/getClosestServer')
			.then(res => res.servers);
	}

	public static shareContent = (requestData: Api.SharedData) => {
		const url = UrlUtils.getShareServiceUrl(ApiUtils.galleryConfig);
		return Utils.fetchText(url, 'POST', true, true, {}, JSON.stringify(requestData)); // requestData needs to be stringified
	}

	public static getCrmAccounts = (): Promise<Api.CrmContact[]> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getCrmAccountsUrl(galleryConfig);
		return Utils.fetchJson<Api.getCrmContactsData>(url, 'GET', true, true)
			.then(crmAccounts => Array.isArray(crmAccounts) ? crmAccounts : []);
	}

	public static createPrivateFolder = (parentGId: number, name: string, resourcesGIds: number[]): Promise<number> => {
		const { galleryConfig, galleryConfig: { clientId } } = ApiUtils;

		if (parentGId) {
			const requestData: Api.CreateFolderData = {
				driveLabel: name,
				clientId,
				parentGId,
				resourcesGIds: resourcesGIds,
			};
			const url = UrlUtils.getCreatePrivateFolderUrl(galleryConfig);
			return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(requestData), false)
				.then(newFolderIdString => parseInt(newFolderIdString, 10));
		}

		return Promise.reject();
	}

	public static createExternalLink = (folderGId: number, label: string, linkUrl: string): Promise<number> => {
		const url = UrlUtils.getCreateExternalLinkUrl(ApiUtils.galleryConfig);
		const data = {
			clientId: ApiUtils.galleryConfig.clientId,
			folderGId,
			label,
			url: linkUrl,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static getIndesignEditorData = (clientId: number, publicationGId: number): Promise<Api.IndesignEditorData> => {
		const dataInUrl = {
			clientId,
			publicationGId,
		};

		return Utils.fetchJson(UrlUtils.getIndesignEditorDataUrl(ApiUtils.galleryConfig), 'GET', true, true, dataInUrl);
	}

	public static getHtmlEditorData = (clientId: number, pageGId: number): Promise<Api.HtmlEditorData> => {
		const dataInUrl = {
			clientId,
			pageGId,
		};

		return Utils.fetchJson(UrlUtils.getHtmlEditorDataUrl(ApiUtils.galleryConfig), 'GET', true, true, dataInUrl);
	}

	public static getEditorResource = (resourceId: number, relPath: string): Promise<string> => {
		const url = UrlUtils.getEditorResourceUrl(ApiUtils.galleryConfig, resourceId, relPath);

		return Utils.fetchText(url, 'GET', true, true, { token: ApiUtils.token });
	}

	public static saveIndesignEditorPage = (clientId: number, resourceGId: number, pageNum: number, htmlContent: string): Promise<string> => {
		const url = UrlUtils.getSaveIndesignEditorPageUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			pageNum,
			htmlContent,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static saveIndesignEditorConfigs = (clientId: number, resourceGId: number, configs: string): Promise<string> => {
		const url = UrlUtils.getSaveIndesignEditorConfigsUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId: resourceGId,
			configsJsonContent: configs,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static saveHtmlEditorPage = (clientId: number, pageGId: number, htmlContent: string): Promise<string> => {
		const url = UrlUtils.getSaveHtmlEditorPageUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			pageGId,
			htmlContent,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static importFile(
		clientId: number,
		folderId: number,
		file: File,
		onProgress?: (progress: ProgressEvent) => void,
		requestId?: string,
		isOptimizedImageImport?: boolean,
	): Promise<Api.ImportFileData> {
		const url = UrlUtils.getImportFileUrl(ApiUtils.galleryConfig, isOptimizedImageImport);
		const formData = new FormData();
		formData.append('clientId', clientId.toString());
		formData.append('folderGId', folderId.toString());
		formData.append('file', file);
		formData.append('filename', file.name);

		return Utils.fetchJson(url, 'POST', true, true, null, formData, true, true, true, false, onProgress, requestId);
	}

	public static createCombo = (clientId: number, resourceGId: number, resourcesGIds: number[], newLabel: string): Promise<Api.AspDriveResourceItem> => {
		const url = UrlUtils.getCreateComboUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId: resourceGId,
			resourcesGIds,
			newLabel,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static getPublicationProgress = async (
		clientId: number,
		publicationGId: number,
		onUpdate: (percentage: number) => void,
		onError: () => void,
		onFinish: () => void,
		shouldCancel?: (state: StoreState) => boolean,
		onCancel?: () => void,
	): Promise<void> => {
		const GET_PROGRESS_DELAY: number = 2000;
		let getProgressTimer: number;

		const url = UrlUtils.getPublicationProgressUrl(ApiUtils.galleryConfig);
		const dataInUrl = {
			clientId,
			publicationGId,
		};

		const getProgress = async (): Promise<void> => {
			try {
				if (shouldCancel && shouldCancel(store.getState())) {
					clearInterval(getProgressTimer);

					if (onCancel) {
						onCancel();
					}
				}

				const { status, percentage } = await Utils.fetchJson<Api.PublicationProgressData>(url, 'GET', true, true, dataInUrl);

				if (status === Api.AssetItemStatus.PROCESSING) {
					onUpdate(percentage);
				} else {
					clearInterval(getProgressTimer);

					if (status === Api.AssetItemStatus.LIVE) {
						onFinish();
					}

					if (status === Api.AssetItemStatus.ERROR) {
						onError();
					}
				}
			} catch (error) {
				onError();
			}
		};

		getProgressTimer = window.setInterval(() => {
			getProgress();
		}, GET_PROGRESS_DELAY);
	}

	public static getProposalTemplates = (): Promise<Api.ProposalTemplatesData> => {
		const { galleryConfig, galleryConfig: { applicationGallery} } = ApiUtils;
		const url = UrlUtils.getProposalTemplatesUrl(galleryConfig, applicationGallery);

		return Utils.fetchJson(url, 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static setAsTemplate = (clientId: number, globalId: number): Promise<void> => {
		const url = UrlUtils.getSetAsTemplateUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			globalId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static removeTemplate = (clientId: number, globalId: number): Promise<void> => {
		const url = UrlUtils.getRemoveTemplateUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			globalId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static createProposalFromMasterProposal = (clientId: number, applicationGId: number, folderGId: number | undefined, publicationGId: number, newLabel: string): Promise<{
		ancestorId: number;
		resource: Api.AspDriveResourceItem;
	}> => {
		const url = UrlUtils.getCreateProposalFromMasterProposalUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			applicationGId,
			folderGId,
			publicationGId,
			newLabel,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static createProposalFromDesktopFile = (
		file: File,
		clientId: number,
		folderId: number,
		onProgress: (progress: ProgressEvent) => void,
		requestId: string,
): Promise<Api.AspDriveResourceItem> => {
		const url = UrlUtils.getCreateProposalFromDesktopFileUrl(ApiUtils.galleryConfig);
		const formData  = new FormData();
		formData.append('clientId', clientId.toString());
		formData.append('parentDriveGId', folderId.toString());
		formData.append('file', file);
		formData.append('filename', file.name);
		formData.append('creationSource', Api.CreationSource.DESKTOP);

		return Utils.fetchJson(url, 'POST', true, true, null, formData, true, true, true, false, onProgress, requestId);
	}

	public static createProposalFromDriveItem = (
		folderId: number,
		clientId: number,
		resourceGId: number,
		newLabel: string,
		requestId: string
	): Promise<Api.AspDriveResourceItem> => {
		const url = UrlUtils.getCreateProposalFromDriveItemUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			newLabel,
			parentDriveGId: folderId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false, true, true, false, undefined, requestId);
	}

	public static shareProposal = (clientId: number, resourceGId: number, name: string): Promise<Api.ShareProposalData> => {
		const url = UrlUtils.getShareProposalUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			name,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static renameResource = (clientId: number, resourceGId: number, resourceLabel: string): Promise<Api.RenameResourceData> => {
		const url = UrlUtils.getRenameResourceUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			resourceLabel,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static trashResources = (clientId: number, resourcesGIds: number[]): Promise<Api.TrashResourcesData> => {
		const url = UrlUtils.getTrashResourcesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourcesGIds,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static removePublicationPages = (clientId: number, publiGId: number, pages: number[]): Promise<void> => {
		const url = UrlUtils.getRemovePublicationPagesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
			pages,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static movePublicationPages = (clientId: number, publiGId: number, pages: number[], newPosition: number): Promise<void> => {
		const url = UrlUtils.getMovePublicationPagesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
			pages,
			newPosition,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static duplicatePublicationPage = (clientId: number, publiGId: number, page: number): Promise<void> => {
		const url = UrlUtils.getDuplicatePublicationPageUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
			page,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static getProfileData = (clientId: number): Promise<Api.ProfileData> => {
		const url = UrlUtils.getProfileDataUrl(ApiUtils.galleryConfig);

		return Utils.fetchJson(url, 'GET', true, true, { clientId });
	}

	public static editProfileData = (
		id: number,
		clientId: number,
		fields: ProfilePageFields,
		userAvatar: UserAvatarState,
	): Promise<Api.ProfileData> => {
		const url = UrlUtils.getEditProfileDataUrl(ApiUtils.galleryConfig);
		const data = {} as Api.EditProfileData;

		if (userAvatar.isEdited && !userAvatar.isRemoved) {
			const { config: { name, width, height, x, y } } = userAvatar;
			fields.photoConfig = JSON.stringify({ name, width, height, x, y });
		}

		if (userAvatar.isNew && userAvatar.file) {
			data.profilePic = userAvatar.file;
		}

		if (userAvatar.isRemoved) {
			data.removeProfilePic = 'true';
		}

		data.profileInfo = JSON.stringify({
			id,
			clientId,
			...fields,
		});

		const formData = Object.entries(data).reduce((formData, [key, value]) => {
			formData.append(key, value);
			return formData;
		}, new FormData());

		return Utils.fetchJson(url, 'PUT', true, true, null, formData);
	}

	public static getProposalsByType = (
		clientId: number,
		proposalStatus: Api.ProposalStatus,
		sortOn: string,
		ascending: boolean,
		pageNum: number,
		itemsPerPage: number,
	): Promise<Api.ProposalsByTypeData> => {
		const url = UrlUtils.getProposalsByTypeUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			proposalStatus,
			sortOn,
			ascending,
			pageNum,
			itemsPerPage,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	// add a special flag for 'editable app' (app with 'Multiple' type and private dir)
	// to get all resources including expired and unpublished
	public static moveResourcesOrder = (clientId: number, parentDriveGId: number, resourcesGIds: number[], positionBefore: number): Promise<void> => {
		const url = UrlUtils.getMoveResourcesOrderUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			parentDriveGId,
			resourcesGIds,
			positionBefore,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	public static moveResources = (clientId: number, newParentGId: number, resourcesGIds: number[]): Promise<void> => {
		const url = UrlUtils.getMoveResourcesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			newParentGId,
			resourcesGIds,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), false);
	}

	// add a special flag for 'Multiple' app type to get all resources including expired and unpublished
	private static getIncludeAllResourcesParam(): {} {
		const {
			appInfos,
			galleryInfos: { config },
			driveList,
		} = store.getState();
		const hasPrivateDir = !!Utils.getPrivateDirId(driveList, config);

		if (userAgentUtils.isMobileApp && Utils.isMultiApp(appInfos)) {
			return { excludeNP: false };
		}

		return Utils.isMultiApp(appInfos) && hasPrivateDir ? { excludeNP: false } : {};
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/api.ts